import { useCallback, useEffect, useMemo, useState, useRef } from 'react';
import { Box, ButtonBase, InputBase, makeStyles, TextField, FormControlLabel, Checkbox } from '@material-ui/core';
import _, { isArray, isFunction } from 'lodash';
import { useSelector } from 'react-redux';
import moment from 'moment';
import { AutoCompleteSelect } from '../../../../components';
import DiaLog from '../../../../components/Dialog';
import { i18n } from '../../../../language';
import StyleButton from '../../../../components/StyleButton';
import { AddIcon, CloseIcon, DropDownIcon, DropUpIcon, ScanIcon } from '../../../../components/Icon';
import useBarcodeListener from '../../../../utils/useBarcodeListener';
import { useScanPage } from '../../../../utils/useScanQrcode';
import EquipmentAsset from '../../../../components/EquipmentAsset';
import { equipmentAPI, systemAPI } from '../../../../apis';
import {
  ADS_LAST_ORDER_STATUS,
  EQUIPMENT_LENGTH,
  EQUIPMENT_REQUEST_DATE_FORMAT,
  REMARKS_FIELD,
  REQUEST_STATUS_ENUM,
} from '../../../../comm/constant';
import useMobile from '../../../../utils/useMobile';
import {
  DWP_COMM_REMARK_BMS_ISFA,
  DWP_COMM_REMARK_BMS_L,
  DWP_COMM_REMARK_BMS_M,
  DWP_COMM_REMARK_BMS_S,
} from '../../../../comm/commandQRcode';
import SingleADSDialog from '../ADS/SingleADSDialog';
import AutoCompleteInput from '../../../../components/AutoCompleteInput';
import { getUUID, initValue, useGetUserInfo } from '../../../../utils';
import { TransferHistory } from '../HistoryLog';
import { handleScanHkid, handleScanPatientQrCode } from '../NewLoanRequestDialog';
import RedStar from '../../../../components/RedStar';
import MultipleInput from '../../../../components/MultipleInput';
import useGetRemarksListAPI from '../../hooks/useGetRemarksListAPI';
import AddRemarks from '../../../../components/AddRemarks';
import { handleDeleteReceipt, handleMultiReceiptEnter, getMutiReceiptVal } from '../Util';

const useStyles = makeStyles(() => ({
  title: {
    fontFamily: 'NotoSans-Medium',
    fontSize: '1rem',
    color: '#7d86a9',
  },
  date: {
    fontFamily: 'NotoSans-Medium',
    fontSize: '1.125rem',
    color: '#2e4469',
  },
  wardAutoSelect: {
    marginTop: '1rem',
  },
  inputBase: ({ error }) => ({
    border: error ? 'solid 1px #f44336' : 'solid 1px #e4e7eb',
    borderRadius: '10px',
    height: '3.5rem',
    width: '24.5rem',
    padding: '0 1.25rem',
    fontFamily: 'NotoSans-Medium',
    fontSize: '1rem',
    color: '#2e4469',
  }),
  errorMsg: {
    color: '#f44336',
    margin: '0.5rem 0.5rem 0',
    fontFamily: 'NotoSans-Medium',
    fontWeight: '500',
  },
  otherInfoBtn: {
    cursor: 'pointer',
  },
  dropDownButton: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    width: '1.5rem',
    height: '1.5rem',
    marginRight: '1.25rem',
  },
  dropDownIcon: {
    fontSize: '0.938rem',
    color: '#3e3e3e',
  },
  otherInformationTitle: {
    fontSize: '1.125rem',
    color: '#3e3e3e',
  },
  scanButton: {
    marginBottom: '0.375rem',
    flexBasis: '66%',
    marginLeft: '1.5rem',
    marginTop: '1rem',
  },
  scanIcon: {
    color: '#538fff',
  },
  star: {
    color: '#ff0300',
  },
  requestID: {
    fontFamily: 'NotoSans-Medium',
    fontSize: '1.313rem',
    color: '#1973e6',
    height: '1.75rem',
  },
  status: {
    fontFamily: 'NotoSans-Medium',
    fontSize: '1rem',
    color: '#2e4469',
    height: '1.75rem',
    display: 'flex',
    alignItems: 'center',
  },
  statusIcon: {
    width: '0.625rem',
    height: '0.625rem',
    minWidth: '0.625rem',
    minHeight: '0.625rem',
    borderRadius: '50%',
    marginRight: '0.625rem',
  },
  textField: {
    borderRadius: '0.625rem',
    border: 'solid 1px #e4e7eb',
    padding: '0 1rem',
    '& .MuiInputBase-root': {
      fontFamily: 'NotoSans-Medium',
      fontSize: '1rem',
      color: '#2e4469',
      lineHeight: '1.8rem',
    },
    '& :before': {
      content: 'none',
    },
    '& :after': {
      content: 'none',
    },
  },
  adsContent: {
    fontFamily: 'NotoSans-Medium',
    fontSize: '1rem',
    color: '#2e4469',
  },
  addTransfer: {
    fontFamily: 'NotoSans-Medium',
    fontSize: '1rem',
    color: '#1973e6',
  },
  addIcon: {
    width: '1.125rem',
    height: '1.125rem',
    color: '#1973e6',
    marginRight: '0.513rem',
  },
  checkBox: {
    '& .MuiCheckbox-root': {
      color: '#c0c6cb',
    },
    '& .MuiTypography-root': {
      fontFamily: 'NotoSans-Medium',
      fontSize: '1rem',
      fontWeight: '500',
      color: '#2e4469',
    },
    '& .Mui-checked': {
      color: '#28d2ab',
    },
  },
  xing: {
    color: 'red',
    marginRight: '0.5rem',
    marginTop: '0.5rem',
  },
}));

const TitleInput = ({
  title,
  placeholder,
  flexBasis,
  value,
  onChange,
  endAdornment,
  enterHandler,
  onFocus,
  onBlur,
  length = 'unset',
  error,
  errorMsg,
  readOnly = false,
  id,
  required = false,
}) => {
  const classes = useStyles({ error });

  const inputHandler = (event) => {
    if (isFunction(onChange)) {
      onChange(event.target.value);
    }
  };

  const onKeyDownHandler = (event) => {
    if ((event.code === 'Enter' || event.code === 'NumpadEnter') && isFunction(enterHandler)) {
      enterHandler(event.target.value);
    }
  };

  return (
    <Box display="flex" flexDirection="column" flexBasis={flexBasis}>
      <div className={classes.title}>
        {title}
        {required && <RedStar />}
      </div>
      <InputBase
        id={id}
        className={classes.inputBase}
        placeholder={placeholder}
        style={{ width: '100%', marginTop: '1.063rem' }}
        onChange={inputHandler}
        value={value}
        endAdornment={endAdornment}
        onKeyDown={onKeyDownHandler}
        onFocus={onFocus}
        onBlur={onBlur}
        inputProps={{ maxLength: length }}
        readOnly={readOnly}
      />
      {error && <div className={classes.errorMsg}>{i18n.t(errorMsg)}</div>}
    </Box>
  );
};

const replaceStrBmss = DWP_COMM_REMARK_BMS_S;
const replaceStrBmsm = DWP_COMM_REMARK_BMS_M;
const replaceStrBmsl = DWP_COMM_REMARK_BMS_L;
const replaceStrBmsIsfa = DWP_COMM_REMARK_BMS_ISFA;

const replaceHandler = (oldReplaceValue, newReplaceValue, value, enter, temp) => {
  const reg = new RegExp(oldReplaceValue, 'g');
  let str;
  if (value.target) {
    str = value.target.value.replace(reg, newReplaceValue);
  } else if (enter) {
    str = `${temp.remarks + value.replace(reg, newReplaceValue)}\n`;
  } else {
    str = `${temp.remarks + value.replace(reg, newReplaceValue)}`;
  }

  return str;
};

const judgeConstantRemark = (val, constantRemark) => val.indexOf(constantRemark) !== -1;

const handleInputRemark = ({ value, setFormData, enter }) => {
  setFormData((preData) => {
    let temp = { ...preData };
    let val = value.target ? value.target.value : value;

    if (judgeConstantRemark(val, replaceStrBmss)) {
      val = replaceHandler(replaceStrBmss, REMARKS_FIELD.BIPAP_MASK_SIZE_S, value, enter, temp);
    } else if (judgeConstantRemark(val, replaceStrBmsm)) {
      val = replaceHandler(replaceStrBmsm, REMARKS_FIELD.BIPAP_MASK_SIZE_M, value, enter, temp);
    } else if (judgeConstantRemark(val, replaceStrBmsl)) {
      val = replaceHandler(replaceStrBmsl, REMARKS_FIELD.BIPAP_MASK_SIZE_L, value, enter, temp);
    } else if (judgeConstantRemark(val, replaceStrBmsIsfa)) {
      val = replaceHandler(replaceStrBmsIsfa, REMARKS_FIELD.EXCEPT_ISOLATION_WARD, value, enter, temp);
    } else if (val.indexOf(REMARKS_FIELD.START_CODE) !== -1 && val.indexOf(REMARKS_FIELD.END_CODE) !== -1) {
      if (enter) {
        val = temp.remarks + val.replace(REMARKS_FIELD.REPLACE_REMARK_REGEX, (value) => `${value.slice(13, -12)}\n`);
      } else {
        val = value.target
          ? val.replace(REMARKS_FIELD.REPLACE_REMARK_REGEX, (value) => value.slice(13, -12))
          : temp.remarks + val.replace(REMARKS_FIELD.REPLACE_REMARK_REGEX, (value) => value.slice(13, -12));
      }
    }

    temp.remarks = val;
    return { ...temp };
  });
};

const emptyEquipment = {
  category: '',
  categoryName: '',
  availableQty: 0,
  assets: [],
  loanQty: 1,
};

const handleRemoveCategory =
  ({ setEquipmentAssets }) =>
  (index) => {
    if (index === 0) {
      setEquipmentAssets((preData) => (preData.length === 1 ? [emptyEquipment] : [...preData.slice(1)]));
    } else {
      setEquipmentAssets((preData) => [...preData.slice(0, index), ...preData.slice(index + 1)]);
    }
  };

const handleAddAsset =
  ({ setEquipmentAssets }) =>
  ({ categoryId, model, assetId, localCode, remark }) =>
    setEquipmentAssets((preData) => {
      let tempData = [...preData];
      let newAsset = {
        assetId,
        localCode,
        model,
        remark,
      };

      // 已存在對應的category
      if (_.some(tempData, (item) => item.category === categoryId)) {
        let index = _.findIndex(tempData, (item) => item.category === categoryId);

        // 存在對應的 assetId
        if (_.some(tempData[index].assets, (item) => item.assetId === assetId)) {
          return tempData;
        } else {
          // 不存在對應的 assetId
          let loanQty = tempData[index].loanQty;
          let assets = tempData[index].assets;
          if (assets.length < loanQty) {
            tempData[index].assets.push(newAsset);
          }
        }
      }

      return tempData;
    });

const UpdateLoanRequestModelBDialog = ({ dialogCloseHandler, dialogFinishHandler, data, categories }) => {
  const initFormData = {
    requestID: data.requestID,
    status: data.status,
    ward: '',
    provider: '',
    patient: '',
    bedID: '',
    remarks: '',
    adsWardLocationId: 0,
    categorySatelliteStoresId: 0,
  };
  const classes = useStyles();
  const [formData, setFormData] = useState(initFormData);
  const [otherInformationOpen, setOtherInformationOpen] = useState(true);
  const [options, setOptions] = useState({
    ward: [],
    provider: [],
    latestOrderNo: '',
    latestOrderStatus: '',
    isDisabled: '',
  });
  const [equipmentAssets, setEquipmentAssets] = useState([emptyEquipment]);
  const loginInfo = useSelector((state) => state.loginInfo);
  const [dates, setDates] = useState({ requestDate: '-', loanOutDate: '-', returnDate: '-' });
  const [flag, setFlag] = useState(0);
  const [errors] = useState({ patient: '' });
  const [isPad] = useMobile();
  const inputRef = useRef(null);
  const [barcodeOpen, setBarcodeOpen] = useState(true);
  const [getAdsOrderParams, setGetAdsOrderParams] = useState({ loanId: '', hospCode: '' });
  const [adsPopup, setAdsPopup] = useState(false);
  const [initTransferToValue, setInitTransferToValue] = useState('');
  const [wardList, setWardList] = useState({});
  const [transferToValue, setTransferToValue] = useState('');
  const [activeLogOpen, setActiveLogOpen] = useState(true);
  const [transferHistory, setTransferHistory] = useState([]);
  const [receipt, setReceipt] = useState(true);
  const [multiReceiptValue, setMultiReceiptValue] = useState([]);
  const userInfo = useGetUserInfo();
  const [isTransfer, setIsTransfer] = useState(false);
  const [wardValue, setWardValue] = useState('');
  const loginWardId = loginInfo.wardId;
  const [receiptRequire, setReceiptRequire] = useState(false);
  const [canTransfer, setCanTransfer] = useState(false);
  const adsRef = useRef(null);
  const [selectedCategoryWards, setSelectedCategoryWards] = useState([]);

  const requiredPatientHN = useMemo(() => {
    return _.some(selectedCategoryWards, (item) => String(item?.isPatientNo) === '1');
  }, [selectedCategoryWards]);

  useBarcodeListener((e) => {
    const trimmedInput = e.replace(/\s*/g, '');

    if (trimmedInput.startsWith('HN')) {
      formChangeHandler('patient')(trimmedInput);
      return;
    } else if (e.toUpperCase().startsWith('WB ')) {
      handleScanHkid(formChangeHandler('patient'), formChangeHandler('bedID'), e);
      setOtherInformationOpen(true);
    }

    const isRemarkRelated =
      [replaceStrBmss, replaceStrBmsm, replaceStrBmsl, replaceStrBmsIsfa].some((str) => e.includes(str)) ||
      (e.includes(REMARKS_FIELD.START_CODE) && e.includes(REMARKS_FIELD.END_CODE));

    if (isRemarkRelated) {
      setOtherInformationOpen(true);
      inputRef.current.focus();
      formChangeHandler('remarks')(e);
      return;
    }

    if ((formData.status === 2 || formData.status === 6) && /^H\d{6,8}$/.test(trimmedInput)) {
      handleMultiReceiptEnter({ value: trimmedInput, id: getUUID() }, setMultiReceiptValue);
    }
  }, barcodeOpen);
  const activeLogController = () => setActiveLogOpen((value) => !value);

  // barcode 開關
  const barcodeControl = useCallback(
    (props) => () => {
      setBarcodeOpen(props);
    },
    []
  );

  // 根據狀態控制
  const controller = useMemo(() => {
    let ctrl = {
      assetId: false,
      category: false,
      loanQty: false,
      asset: false,
      Decline: false,
      showAssets: false,
      withdraw: false,
      readOnly: false,
      transfer: false,
    };

    switch (formData?.status) {
      case 2:
      case 4:
        break;
      case 3:
      case 5:
        ctrl = { ...ctrl, readOnly: true };
        break;
      case 1:
        ctrl = { ...ctrl, assetId: true, asset: true, showAssets: true, withdraw: true };
        break;
      case 6:
        ctrl = {
          ...ctrl,
          assetId: false,
          category: false,
          loanQty: false,
          asset: false,
          Decline: false,
          requestDate: true,
          loanOutDate: true,
          transfer: true,
        };
        break;
      default:
        break;
    }

    return ctrl;
  }, [formData?.status]);
  // 获取当前时间
  const getTransferHistory = async () => {
    try {
      const { result } = await equipmentAPI.getTransferHistory({ loanId: data.requestID });
      setTransferHistory(result);
    } catch (error) {}
  };

  useEffect(() => {
    getTransferHistory();
  }, []);

  // 判断是否开启receipt
  const sameCategory = () => {
    if (!isArray(categories)) return;
    const arr = categories.filter((item) => equipmentAssets.some((a) => a.category === item.categoryId));
    // Receipt Confirmation 是否必填
    const arrs = arr.some((item) =>
      item.wards.some((i) => i.wardId === formData.provider && i.isReceiptConfirmation === 1)
    );
    setReceiptRequire(arrs);

    // 是否可以transfer
    const isWardTransfer = arr.some((item) =>
      item.wards.some((i) => i.wardId === formData.provider && i.isWardTransfer === 1)
    );
    setCanTransfer(isWardTransfer);
  };
  useEffect(() => {
    sameCategory();
  }, [equipmentAssets, formData.provider]);

  // 獲取detail
  const getDetail = useCallback(async () => {
    try {
      const { result } = await equipmentAPI.getRequestDetail({ loanId: data.requestID });
      const detail = result[0];
      setInitTransferToValue(detail.transferredWardId);
      setTransferToValue(
        detail?.transferredWardLocationId
          ? `${detail?.transferredWardId}-${detail?.transferredWardLocationId}`
          : detail?.transferredWardId
      );
      setMultiReceiptValue(getMutiReceiptVal(detail?.receiptConfirmationBy, 'fromApi'));
      setReceipt(typeof detail?.receiptConfirmationBy === 'string' && detail?.receiptConfirmationBy?.length > 0);
      setGetAdsOrderParams({ loanId: detail.loanId, hospCode: detail.hospCode });
      const { result: adsResult } = await equipmentAPI.getAdsOrder({
        loanId: detail.loanId,
        hospCode: detail.hospCode,
      });

      setFormData((preData) => ({
        ...preData,
        ...detail,
        ward: detail.wardId,
        provider: detail.provideWardId,
        patient: detail.patientNo ?? '',
        bedID: detail.bedNo ?? '',
        remarks: detail.remark ?? '',
        status: detail.status,
        transferredWardId: detail.transferredWardId,
        adsWardLocationId: detail.adsWardLocationId,
        categorySatelliteStoresId: detail.categorySatelliteStoresId,
      }));

      setDates({
        requestDate: detail.requestDate ? moment(detail.requestDate).format(EQUIPMENT_REQUEST_DATE_FORMAT) : '-',
        loanOutDate: detail.transferDatetime
          ? moment(detail.transferDatetime).format(EQUIPMENT_REQUEST_DATE_FORMAT)
          : '-',
        returnDate: detail.returnDatetime ? moment(detail.returnDatetime).format(EQUIPMENT_REQUEST_DATE_FORMAT) : '-',
      });

      setOptions({
        ward: [
          {
            text: detail.roomName
              ? `${detail.hospCode} - ${detail.requestWard} - ${detail.roomName}`
              : `${detail.hospCode} - ${detail.requestWard}`,
            value: detail.wardId,
          },
        ],
        provider: [
          {
            text: detail.satelliteStoresName ? `    ${detail.satelliteStoresName}` : detail.provideWard,
            value: detail.provideWardId,
            provideWardName: detail.satelliteStoresName ? detail.satelliteStoresName : detail.provideWard,
          },
        ],
        latestOrderNo: adsResult.latestOrderNo,
        latestOrderStatus: adsResult.latestOrderStatus,
        isDisabled: adsResult.isDisabled,
      });

      let tempAssets = _.map(detail.categories, (item) => ({
        category: item.categoryId,
        assets: _.map(item.equipments, (equipmentItem) => ({
          ...equipmentItem,
          remark: equipmentItem?.equipmentRemark,
        })),
        loanQty: item.assetCount,
        categoryName: item.categoryName,
        remark: item.remark,
      }));

      if (tempAssets.length > 0) {
        setEquipmentAssets(tempAssets);
      }
    } catch (error) {
      console.log(error);
    }
  }, [data]);

  const otherInformationController = () => setOtherInformationOpen((open) => !open);

  // 處理 assets 相關操作
  const equipmentAssetsHandler = useCallback(({ type, flag }) => {
    switch (type) {
      case 'addEquipment':
        setEquipmentAssets((preData) => {
          return [...preData, emptyEquipment];
        });
        return;
      case 'changeCategory':
        return (value, categoryName) =>
          setEquipmentAssets((preData) => {
            let tempData = [...preData];

            tempData.splice(flag, 1, {
              ...preData[flag],
              category: value,
              categoryName,
            });
            return tempData;
          });
      case 'removeCategory':
        return handleRemoveCategory({ setEquipmentAssets });
      case 'addAsset':
        return handleAddAsset({ setEquipmentAssets });
      case 'removeAsset':
        return (id) =>
          setEquipmentAssets((preData) => {
            let tempData = [...preData];
            tempData[flag].assets = _.filter(tempData[flag].assets, (item) => item.assetId !== id);
            return tempData;
          });
      case 'updateAvailableQty':
        return (newAvailableQty) =>
          setEquipmentAssets((preData) => {
            let tempData = [...preData];

            tempData.splice(flag, 1, {
              ...preData[flag],
              availableQty: newAvailableQty,
            });
            return tempData;
          });
      case 'updateLoanQty':
        return (newLoanQty) =>
          setEquipmentAssets((preData) => {
            let tempData = [...preData];
            let assets = tempData[flag].assets;

            if (newLoanQty < assets.length) {
              return tempData;
            }

            tempData.splice(flag, 1, {
              ...preData[flag],
              loanQty: newLoanQty,
            });
            return tempData;
          });
      default:
        return;
    }
  }, []);

  // cancel transfer API
  const cancelTransfer = async () => {
    try {
      await equipmentAPI.cancelTransfer({ loanId: data.requestID });
      dialogFinishHandler();
    } catch (error) {}
  };
  // confirm transfer API
  const confirmTransfer = async () => {
    try {
      await equipmentAPI.confirmTransfer({ loanId: data.requestID });
      dialogFinishHandler();
    } catch (error) {}
  };
  // reject transfer API
  const rejectTransfer = async () => {
    try {
      await equipmentAPI.rejectTransfer({ loanId: data.requestID });
      dialogFinishHandler();
    } catch (error) {}
  };

  // 處理 assets 以外的輸入
  const formChangeHandler = useCallback((key, enter) => {
    return (value) => {
      if (key === 'remarks') {
        handleInputRemark({ value, setFormData, enter });
        return;
      }
      if (key === 'patient') {
        setFormData((preData) => {
          let newValue = value;
          newValue = newValue.toUpperCase();
          newValue = newValue.replace(/[\(\)]+/g, '');
          return { ...preData, [key]: newValue };
        });
      } else {
        setFormData((preData) => ({ ...preData, [key]: value }));
      }
    };
  }, []);

  useEffect(() => {
    (async () => {
      await getDetail();
    })();
  }, []);

  // 獲取 ward list
  const getWardList = useCallback(async () => {
    try {
      const { result } = await systemAPI.getSysHospitalWardList({ hospitalId: loginInfo.hospitalId, wardId: null });
      setWardList(result?.[0] || {});
    } catch (error) {
      console.log(error);
    }
  }, [loginInfo]);

  useEffect(() => {
    (async () => {
      await getWardList();
    })();
  }, []);

  const wardsOptions = useMemo(() => {
    let wards = [];

    if (Array.isArray(wardList?.wards)) {
      wards = _.map(wardList?.wards || [], (item) => {
        const showWardOption = item?.enableYn === 0;
        let roomOptions = [];
        const wardOption = {
          text: `${wardList?.hospitalCode} - ${item.wardName}`,
          value: item.wardId,
          tagId: item.wardId,
          wardId: item.wardId,
          adsWardLocationId: null,
        };
        const hasRoomType = _.some(item.wardLocation, (locationItem) => locationItem.adsRoomType === 'R');
        if (hasRoomType) {
          roomOptions = _.filter(item.wardLocation, (locationItem) => locationItem.adsRoomType === 'R').map(
            (locationItem) => ({
              text: `${wardList?.hospitalCode} - ${item.wardName} - ${locationItem.roomName}`,
              value: locationItem.adsWardLocationId,
              tagId: locationItem.adsWardLocationId,
              wardId: item.wardId,
              adsWardLocationId: locationItem.adsWardLocationId,
            })
          );
        }
        return showWardOption ? [wardOption, ...roomOptions] : [...roomOptions];
      }).flat();
    }

    let wardss = wards.filter((item) => item.value !== formData.ward);

    return wardss;
  }, [wardList, formChangeHandler, formData.provider, formData.ward]);

  // 转移的详细信息
  const transferDetail = _.find(wardsOptions, (item) => item.value === transferToValue);
  // 转移的病房
  const transferWard = _.get(transferDetail, 'wardId');
  // 转移的房间
  const transferRoom = _.get(transferDetail, 'adsWardLocationId');

  // update loan request 的 API
  const updateLoanRequestAPI = useCallback(
    async ({ adsCategoryId, isAdsServer }) => {
      try {
        let params = {
          bedNo: formData.bedID ? formData.bedID : null,
          loanCategorys: _.map(
            _.filter(equipmentAssets, (item) => Boolean(item.category)),
            (item) => ({
              categoryId: item.category,
              categoryName: item.categoryName,
              assetCount: item.loanQty,
              assetIds: _.map(item.assets, (assetItem) => assetItem.assetId),
            })
          ),
          patientNo: formData.patient,
          provideWardId: formData.provider,
          remark: formData.remarks,
          wardId: formData.ward,
          loanId: data.requestID,
          provideWardName: options.provider[0].text,
          wardName: loginInfo.wardName,
          status: formData.status,
          transferredWardId: transferWard ?? null,
          transferredLocationId: transferRoom ?? null,
          receiptConfirmationBy: getMutiReceiptVal(multiReceiptValue, 'toApi'),
          adsCategoryId,
          instCode: userInfo.hospCode,
          isAdsServer,
          loanType: 'BORROW',
        };

        await equipmentAPI.updateRequest(params);
        dialogFinishHandler();
      } catch (error) {
        console.log(error);
      }
    },
    [
      formData,
      equipmentAssets,
      dialogFinishHandler,
      data,
      options,
      loginInfo,
      multiReceiptValue,
      transferToValue,
      wardValue,
      initTransferToValue,
    ]
  );

  // declined loan request 的 API
  const declinedLoanRequestAPI = useCallback(
    async ({ isAdsServer }) => {
      try {
        await equipmentAPI.withdraw({
          loanId: data.requestID,
          loanStatus: 'Withdrawn',
          instCode: userInfo.hospCode,
          isAdsServer,
        });
        dialogFinishHandler();
      } catch (error) {
        console.log(error);
      }
    },
    [data, dialogFinishHandler, controller.withdraw, userInfo.hospCode]
  );

  // cancel 按钮的 copywriting 和 样式
  const cancelMemo = useMemo(() => {
    let copywriting = i18n.t('EQUIPMENT_VIEWREQUEST_UPDATE_REQUEST_CANCEL');
    let style;

    if (formData?.status === 1) {
      copywriting = i18n.t('EQUIPMENT_VIEWREQUEST_UPDATE_REQUEST_WITHDRAW');
      style = { border: 'unset', backgroundColor: '#FF7270', color: '#fff' };
    }

    return {
      copywriting,
      style,
    };
  }, [formData?.status]);

  // 提交表單事件
  const dialogOpenHandler = useCallback(() => {
    if (formData?.status === 1) {
      setAdsPopup(true);
      adsRef?.current?.getAdsLocation?.();
    } else {
      (async () => {
        await updateLoanRequestAPI({});
      })();
    }
  }, [updateLoanRequestAPI]);

  // Scan Equipment 的組件和調用函數,返回第一個是組件，第二個是開啟的函數
  const [ScanPortalHN, openHNScanPage] = useScanPage(
    handleScanPatientQrCode(formChangeHandler('patient'), formChangeHandler('bedID'))
  );
  const [ScanPortalRemarks, openScanPageRemarks] = useScanPage(formChangeHandler('remarks', true));

  const getBedNo = useCallback(async () => {
    try {
      if (formData.patient.toUpperCase().startsWith('WB ')) {
        handleScanHkid(formChangeHandler('patient'), formChangeHandler('bedID'), formData.patient);
      } else {
        const { result } = await equipmentAPI.getBedNo({
          patientNo: formData.patient.replace(/\s*/g, ''),
        });
        formChangeHandler('bedID')(result.BedNo);
      }
    } catch (error) {
      console.log(error);
    }
  }, [formData.patient, formChangeHandler]);

  const confirmDisable = useMemo(
    () => Boolean(errors.patient) || (requiredPatientHN && !formData.patient),
    [errors.patient, requiredPatientHN, formData.patient]
  );

  const statusData = useMemo(
    () =>
      _.find(REQUEST_STATUS_ENUM, (item) => {
        if (formData.status === 2 && loginWardId === formData.oldRequestWardId) {
          return item.enumKey === -1;
        } else if (formData.status === 4 && loginWardId === formData.oldRequestWardId) {
          return item.enumKey === -2;
        }
        return item.enumKey === formData.status;
      }),
    [formData.status, loginWardId, formData.oldRequestWardId]
  );

  const changeWardValue = (value) => {
    setWardValue(value);
    setTransferToValue(value);
  };

  const handleOpenAdsPopup = () => {
    try {
      setAdsPopup(true);
      adsRef?.current?.getAdsLastOrder?.();
    } catch (error) {
      console.log(error);
    }
  };

  let cancelTransferText;
  if (formData.status === 6 && loginWardId === formData.wardId) {
    cancelTransferText = i18n.t('EQUIPMENT_BUTTON_CANCEL_TRANSFER');
  } else if (controller.transfer && (loginWardId === formData.provideWardId || loginWardId === initTransferToValue)) {
    cancelTransferText = i18n.t('EQUIPMENT_BUTTON_REJECT_TRANSFER');
  } else {
    cancelTransferText = false;
  }

  const adsWardInfo = useMemo(() => {
    let provider = '';
    let request = '';

    if (formData.ward) {
      request = _.find(options.ward, (item) => item.value === formData.ward)?.text ?? '';
    }

    if (formData.provider) {
      provider = _.find(options.provider, (item) => item.value === formData.provider)?.provideWardName ?? '';
    }

    return {
      provider,
      request,
    };
  }, [formData.ward, formData.provider, options]);

  const { remarkList: activeRemarkList, getRemarksListAPI } = useGetRemarksListAPI();

  // 选择remark
  const handleSelectRemark = (remarks) => {
    setFormData((o = {}) => ({
      ...o,
      remarks: `${o?.remarks || ''} ${remarks}`.slice(0, 99),
    }));
  };

  useEffect(() => {
    if (equipmentAssets?.[flag]?.category) {
      getRemarksListAPI({ hospitalCategoryIds: [equipmentAssets?.[flag]?.category] });
    }
  }, [equipmentAssets?.[flag]?.category]);

  return (
    <>
      <ScanPortalHN />
      <ScanPortalRemarks />
      <DiaLog
        open={true}
        handleClose={dialogCloseHandler}
        handleCancel={() => (controller.withdraw ? handleOpenAdsPopup() : dialogCloseHandler())}
        handleConfirm={
          controller.transfer && (loginWardId === formData.provideWardId || loginWardId === initTransferToValue)
            ? confirmTransfer
            : dialogOpenHandler
        }
        title={i18n.t('EQUIPMENT_VIEWREQUEST_UPDATE_REQUEST_TITLE')}
        cancel={
          controller.transfer && (loginWardId === formData.provideWardId || loginWardId === initTransferToValue)
            ? false
            : cancelMemo.copywriting
        }
        confirm={
          controller.transfer && (loginWardId === formData.provideWardId || loginWardId === initTransferToValue)
            ? i18n.t('EQUIPMENT_BUTTON_CONFIRM_TRANSFER')
            : i18n.t('EQUIPMENT_VIEWREQUEST_UPDATE_REQUEST_CONFIRM')
        }
        cancelTransfer={cancelTransferText}
        // cancelTransfer={controller.transfer ? 'Cancel Transfer' : false}
        cancelTransferStyle={{ backgroundColor: '#ff7270', border: 'none', color: '#fff' }}
        handleCancelTransfer={
          controller.transfer && (loginWardId === formData.provideWardId || loginWardId === initTransferToValue)
            ? rejectTransfer
            : cancelTransfer
        }
        dialogfootStyle={{ marginTop: '1rem' }}
        confirmStyle={{ backgroundColor: '#28d2ab' }}
        cancelStyle={cancelMemo.style}
        disabled={confirmDisable}
        readOnly={controller.readOnly}
      >
        <Box display="flex" justifyContent="space-between" alignItems="center" pb="1.313rem">
          <Box display="flex" flexDirection="column" width="19.375rem" gridRowGap="0.25rem">
            <div className={classes.title}>{i18n.t('EQUIPMENT_VIEWREQUEST_UPDATE_REQUEST_ID')}</div>
            <div className={classes.requestID}>{data.requestID}</div>
          </Box>
          <Box display="flex" flexDirection="column" width="19.375rem" gridRowGap="0.25rem">
            {/* <div className={classes.title}>{i18n.t('EQUIPMENT_VIEWREQUEST_UPDATE_REQUEST_STATUS')}</div> */}
            <div className={classes.title}>{i18n.t('EQUIPMENT_REQUEST_STATUS')}</div>
            <div className={classes.status}>
              <div
                className={classes.statusIcon}
                style={{
                  backgroundColor: initValue(statusData?.color, '#fff'),
                }}
              />
              {i18n.t(initValue(statusData?.textKey, 'EQUIPMENT_VIEWREQUEST_BLANK'))}
            </div>
          </Box>
        </Box>
        <Box display="flex" justifyContent="space-between" alignItems="center" pb="1.313rem">
          <Box>
            <Box display="flex" justifyContent="space-between" alignItems="center">
              <div className={classes.title}>
                {i18n.t('EQUIPMENT_VIEWREQUEST_UPDATE_REQUEST_WARD')} <span className={classes.star}>*</span>
              </div>
              {!isTransfer &&
                formData.status === 2 &&
                (loginWardId === formData.provideWardId || loginWardId === formData.wardId) &&
                canTransfer && (
                  <ButtonBase className={classes.addTransfer} onClick={() => setIsTransfer(true)}>
                    <AddIcon className={classes.addIcon} />
                    Transfer
                  </ButtonBase>
                )}
            </Box>
            <AutoCompleteSelect
              className={classes.wardAutoSelect}
              id="ViewRequest_ward_autoSelect"
              width="19.375rem"
              height="3.5rem"
              itemList={options.ward}
              placeholder={i18n.t('EQUIPMENT_VIEWREQUEST_UPDATE_REQUEST_WARD_PLACEHOLDER')}
              value={formData.ward}
              onChange={formChangeHandler('ward')}
              disabled={true}
            />
          </Box>
          <Box>
            <div className={classes.title}>
              {i18n.t('EQUIPMENT_VIEWREQUEST_UPDATE_REQUEST_PROVIDER')} <span className={classes.star}>*</span>
            </div>
            <AutoCompleteSelect
              className={classes.wardAutoSelect}
              id="ViewRequest_ward_autoSelect"
              width="19.375rem"
              height="3.5rem"
              itemList={options.provider}
              placeholder={i18n.t('EQUIPMENT_VIEWREQUEST_UPDATE_REQUEST_PROVIDER_PLACEHOLDER')}
              value={formData.provider}
              onChange={formChangeHandler('provider')}
              disabled={true}
            />
          </Box>
        </Box>
        {isTransfer && (
          <Box marginBottom="1rem">
            <Box display="flex" justifyContent="space-between" alignItems="center" width="19.2rem">
              <div className={classes.title}>
                Transfer To <span className={classes.star}>*</span>
              </div>
              <ButtonBase
                className={classes.addTransfer}
                onClick={() => {
                  setIsTransfer(false);
                }}
              >
                <CloseIcon className={classes.addIcon} />
                Cancel
              </ButtonBase>
            </Box>
            <AutoCompleteInput
              style={{ marginTop: '0.5rem' }}
              itemList={wardsOptions}
              value={wardValue}
              width="19.375rem"
              height="3.5rem"
              onChange={changeWardValue}
              placeholder="Please select"
            />
          </Box>
        )}
        {options.latestOrderNo && options.latestOrderStatus && (
          <Box display="flex" justifyContent="space-between" alignItems="center" pb="0.5rem">
            <Box display="flex" flexDirection="column" width="19.375rem" gridRowGap="0.25rem">
              <div className={classes.title}>{i18n.t('EQUIPMENT_ADS_POPUP_LAST_ORDER')}</div>
              <div className={classes.adsContent}>{options.latestOrderNo}</div>
            </Box>
            <Box display="flex" flexDirection="column" width="19.375rem" gridRowGap="0.25rem">
              <div className={classes.title}>{i18n.t('EQUIPMENT_ADS_POPUP_LAST_STATUS')}</div>
              <div className={classes.adsContent}>
                {ADS_LAST_ORDER_STATUS[options.latestOrderStatus]
                  ? i18n.t(ADS_LAST_ORDER_STATUS[options.latestOrderStatus])
                  : '-'}
                {options.isDisabled === 'Y' ? i18n.t('EQUIPMENT_ADS_STATUS_DELIVERED') : ''}
              </div>
            </Box>
          </Box>
        )}
        {formData.status === 6 && (
          <Box marginBottom="1rem">
            <Box display="flex" justifyContent="space-between" alignItems="center" width="19.2rem">
              <div className={classes.title}>
                Transfer To <span className={classes.star}>*</span>
              </div>
            </Box>
            <AutoCompleteInput
              style={{ marginTop: '0.5rem' }}
              itemList={wardsOptions}
              value={transferToValue}
              width="19.375rem"
              height="3.5rem"
              onChange={changeWardValue}
              placeholder="Please select"
              disabled={loginWardId !== formData.wardId && loginWardId !== formData.provideWardId}
            />
          </Box>
        )}
        <Box
          display="flex"
          justifyContent="space-between"
          gridColumnGap="1rem"
          mt="1.25rem"
          pb="0.938rem"
          borderBottom="solid 1px #e6e7e8"
        >
          <Box display="flex" flexDirection="column" flexBasis="33%" gridRowGap="1rem">
            <div className={classes.title}>{i18n.t('EQUIPMENT_VIEWREQUEST_UPDATE_REQUEST_REQUEST_DATE')}</div>
            <div className={classes.date}>{dates.requestDate}</div>
          </Box>
          <Box display="flex" flexDirection="column" flexBasis="33%" gridRowGap="1rem">
            <div className={classes.title}>{i18n.t('EQUIPMENT_VIEWREQUEST_UPDATE_REQUEST_LOAN_OUT_DATE')}</div>
            <div className={classes.date}>{dates.loanOutDate}</div>
          </Box>
          <Box display="flex" flexDirection="column" flexBasis="33%" gridRowGap="1rem">
            <div className={classes.title}>{i18n.t('EQUIPMENT_VIEWREQUEST_UPDATE_REQUEST_RETURN_DATE')}</div>
            <div className={classes.date}>{dates.returnDate}</div>
          </Box>
        </Box>
        {formData.status === 6 || formData.status === 2 || formData.status === 4 ? (
          <Box>
            <FormControlLabel
              control={<Checkbox />}
              label={
                <>
                  {i18n.t('EQUIPMENT_VIEWREQUEST_UPDATE_REQUEST_REQUESTOR_RECEIPT')}
                  {receiptRequire && <span className={classes.star}>*</span>}
                </>
              }
              checked={receipt}
              onChange={(e) => setReceipt(e.target.checked)}
              className={classes.checkBox}
            />
            {receipt && (
              <MultipleInput
                placeholder="Input name or scan QR code"
                value={multiReceiptValue}
                handleEnter={(val) => handleMultiReceiptEnter(val, setMultiReceiptValue)}
                handleDelete={(val) => handleDeleteReceipt(val, setMultiReceiptValue)}
                readOnly={formData.status === 4}
              />
            )}
          </Box>
        ) : (
          ''
        )}
        <Box display="flex" flexDirection="column" pt="1.25rem" pb="1.25rem">
          <EquipmentAsset
            data={equipmentAssets}
            flag={flag}
            setFlag={setFlag}
            changeDataHandler={equipmentAssetsHandler}
            provider={formData.provider}
            status={formData.status}
            controller={controller}
            modelB={true}
            setSelectedCategoryWards={setSelectedCategoryWards}
            categorySatelliteStoresId={formData.categorySatelliteStoresId}
          />
        </Box>
        <Box
          id="equipment-button-otherinformation"
          className={classes.otherInfoBtn}
          display="flex"
          p="1.125rem 0.813rem"
          borderTop="solid 1px #e6e7e8"
          borderBottom="solid 1px #e6e7e8"
          onClick={otherInformationController}
        >
          <div className={classes.dropDownButton}>
            {otherInformationOpen ? (
              <DropUpIcon className={classes.dropDownIcon} />
            ) : (
              <DropDownIcon className={classes.dropDownIcon} />
            )}
          </div>
          <div className={classes.otherInformationTitle}>
            {i18n.t('EQUIPMENT_VIEWREQUEST_UPDATE_REQUEST_OTHER_INFORMATION')}
          </div>
        </Box>
        {otherInformationOpen && (
          <Box display="flex" flexDirection="column" p="1rem 0 1.5rem" borderBottom="solid 1px #e6e7e8">
            <Box display="flex" justifyContent="space-between" mb="1.25rem">
              <TitleInput
                id="equipment-input-patient"
                title={i18n.t('EQUIPMENT_VIEWREQUEST_UPDATE_REQUEST_PATIENT_HN')}
                placeholder={i18n.t('EQUIPMENT_VIEWREQUEST_UPDATE_REQUEST_PATIENT_HN_PLACEHOLDER')}
                flexBasis="48%"
                endAdornment={
                  isPad ? (
                    <ButtonBase
                      id="equipment-button-patient"
                      onClick={() => {
                        (async () => {
                          await openHNScanPage();
                        })();
                      }}
                    >
                      <ScanIcon className={classes.scanIcon} />
                    </ButtonBase>
                  ) : (
                    <div />
                  )
                }
                value={formData.patient}
                onChange={formChangeHandler('patient')}
                length={EQUIPMENT_LENGTH.REQUEST_HN}
                enterHandler={getBedNo}
                onFocus={barcodeControl(false)}
                onBlur={() => {
                  barcodeControl(true)();
                  setFormData((preData) => ({ ...preData, patient: preData.patient.replace(/\s*/g, '') }));
                }}
                error={Boolean(errors.patient)}
                errorMsg={errors.patient}
                readOnly={controller.readOnly}
                required={requiredPatientHN}
              />
              <TitleInput
                id="equipment-input-bed"
                title={i18n.t('EQUIPMENT_VIEWREQUEST_UPDATE_REQUEST_BED_ID')}
                placeholder={i18n.t('EQUIPMENT_VIEWREQUEST_UPDATE_REQUEST_BED_ID_PLACEHOLDER')}
                flexBasis="48%"
                value={formData.bedID}
                onChange={formChangeHandler('bedID')}
                length={EQUIPMENT_LENGTH.REQUEST_BED}
                readOnly={controller.readOnly}
              />
            </Box>
            {/* <TitleInput
              id="equipment-input-remark"
              title={i18n.t('EQUIPMENT_VIEWREQUEST_UPDATE_REQUEST_REMARK')}
              placeholder={i18n.t('EQUIPMENT_VIEWREQUEST_UPDATE_REQUEST_REMARK_PLACEHOLDER')}
              value={formData.remarks}
              onChange={formChangeHandler('remarks')}
              length={EQUIPMENT_LENGTH.REQUEST_REMARK}
              readOnly={controller.readOnly}
            /> */}
            <Box display="flex" justifyContent="space-between" flexDirection="column" mb="1.313rem">
              <Box display="flex" justifyContent="space-between">
                <div className={classes.title}>{i18n.t('EQUIPMENT_VIEWREQUEST_UPDATE_REQUEST_REMARK')}</div>
                <AddRemarks list={activeRemarkList} handleSelect={handleSelectRemark} />
              </Box>
              <Box display="flex">
                <TextField
                  id="ViewRequest-input-remark"
                  className={classes.textField}
                  placeholder={i18n.t('EQUIPMENT_VIEWREQUEST_UPDATE_REQUEST_REMARK_PLACEHOLDER')}
                  multiline={true}
                  fullWidth={true}
                  rows={4}
                  value={formData.remarks}
                  onChange={formChangeHandler('remarks')}
                  style={{ marginTop: '1rem' }}
                  readOnly={controller.readOnly}
                  inputRef={inputRef}
                  onFocus={() => setBarcodeOpen(false)}
                  onBlur={() => setBarcodeOpen(true)}
                />
                {isPad && (
                  <StyleButton
                    id="ViewRequest-button-scan-remark"
                    className={classes.scanButton}
                    width="14.125rem"
                    text={i18n.t('EQUIPMENT_VIEWREQUEST_NEW_REQUEST_SCAN_EQUIPMENT')}
                    Icon={ScanIcon}
                    iconStyle={{ fontSize: '1.5rem', marginRight: '0.75rem' }}
                    onClick={openScanPageRemarks}
                    style={{ marginTop: '1rem' }}
                  />
                )}
              </Box>
            </Box>
          </Box>
        )}
        <Box
          id="equipment-button-activeLog"
          className={classes.otherInfoBtn}
          display="flex"
          p="1.125rem 0.813rem"
          borderBottom="solid 1px #e6e7e8"
          onClick={activeLogController}
        >
          <div className={classes.dropDownButton}>
            {activeLogOpen ? (
              <DropUpIcon className={classes.dropDownIcon} />
            ) : (
              <DropDownIcon className={classes.dropDownIcon} />
            )}
          </div>
          <div className={classes.otherInformationTitle}>{i18n.t('EQUIPMENT_ACTIVITY_LOG')}</div>
        </Box>
        {activeLogOpen && <TransferHistory transferHistoryLogList={transferHistory} />}
      </DiaLog>
      <SingleADSDialog
        handleNewRequest={updateLoanRequestAPI}
        handleDelete={declinedLoanRequestAPI}
        equipmentAssets={equipmentAssets}
        formData={formData}
        handleClose={() => setAdsPopup(false)}
        adsType="withdraw"
        adsWardInfo={adsWardInfo}
        getAdsOrderParams={getAdsOrderParams}
        adsPopup={adsPopup}
        ref={adsRef}
        provideWardId={formData.provider}
        requestWard={formData.ward}
        adsWardLocationId={formData.adsWardLocationId}
        categorySatelliteStoresId={formData.categorySatelliteStoresId}
      />
    </>
  );
};

export default UpdateLoanRequestModelBDialog;
